// src/features/auth/components/PasswordResetConfirm.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import PasswordStrengthIndicator, { PasswordStrength } from './PasswordStrengthIndicator';
import PasswordRequirements from './PasswordRequirements';
import RateLimitFeedback from './RateLimitFeedback';
import { authErrorHandlers, AuthError, isRateLimitError } from '../utils/authErrorUtils';
import { AUTH_MESSAGES } from '../constants/messages';

const PasswordResetConfirm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [tokenValidating, setTokenValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [showRequirements, setShowRequirements] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setAuthError({
          message: AUTH_MESSAGES.PASSWORD_RESET.TOKEN_INVALID,
          type: 'validation'
        });
        setTokenValidating(false);
        return;
      }

      try {
        await authService.verifyResetToken({ token });
        setTokenValid(true);
        setAuthError(null);
      } catch (err: any) {
        const error = authErrorHandlers.tokenVerification(err);
        setAuthError(error);
      } finally {
        setTokenValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form fields
    if (!password || !confirmPassword) {
      setAuthError({
        message: AUTH_MESSAGES.PASSWORD_RESET.FIELDS_REQUIRED,
        type: 'validation'
      });
      return;
    }

    if (password !== confirmPassword) {
      setAuthError({
        message: AUTH_MESSAGES.PASSWORD_RESET.PASSWORDS_DONT_MATCH,
        type: 'validation'
      });
      return;
    }

    // Validate password strength
    if (passwordStrength && (passwordStrength.level === 'Very Weak' || passwordStrength.level === 'Invalid')) {
      setAuthError({
        message: AUTH_MESSAGES.PASSWORD_RESET.WEAK_PASSWORD,
        type: 'validation',
        field: 'password'
      });
      return;
    }

    if (!token) {
      setAuthError({
        message: AUTH_MESSAGES.PASSWORD_RESET.TOKEN_INVALID,
        type: 'validation'
      });
      return;
    }

    setPasswordSubmitting(true);
    setAuthError(null);

    try {
      await authService.resetPassword({
        token,
        password: password
      });
      setSuccess(true);
    } catch (err: any) {
      const error = authErrorHandlers.passwordUpdate(err);
      setAuthError(error);
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth', { replace: true });
  };

  // Handle password strength calculation
  useEffect(() => {
    if (password) {
      // Calculate password strength inline using the same logic as PasswordStrengthIndicator
      const criteria = [
        { test: (pwd: string) => pwd.length >= 8, met: false },
        { test: (pwd: string) => /[A-Z]/.test(pwd), met: false },
        { test: (pwd: string) => /[a-z]/.test(pwd), met: false },
        { test: (pwd: string) => /\d/.test(pwd), met: false },
        { test: (pwd: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd), met: false }
      ];
      
      criteria.forEach(criterion => {
        criterion.met = criterion.test(password);
      });
      
      const score = criteria.filter(c => c.met).length;
      let level: PasswordStrength['level'];
      
      switch (score) {
        case 0:
        case 1:
          level = 'Very Weak';
          break;
        case 2:
          level = 'Weak';
          break;
        case 3:
          level = 'Fair';
          break;
        case 4:
          level = 'Good';
          break;
        case 5:
          level = 'Strong';
          break;
        default:
          level = 'Very Weak';
      }
      
      setPasswordStrength({ score, level, color: '', criteria: [] });
    } else {
      setPasswordStrength(null);
    }
  }, [password]);

  const handleRateLimitRetry = () => {
    setAuthError(null);
  };

  if (tokenValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg dark:bg-dark-theme-bg px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary shadow-xl rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light dark:border-primary-dark mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text mb-2">
              {AUTH_MESSAGES.PASSWORD_RESET.VALIDATING_TOKEN}
            </h2>
            <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
              Please wait while we validate your password reset token...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid || authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg dark:bg-dark-theme-bg px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary shadow-xl rounded-lg p-8">
            <div className="text-center mb-6">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-2">
                Invalid Reset Link
              </h2>
              <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
                {authError?.message || AUTH_MESSAGES.PASSWORD_RESET.TOKEN_INVALID}
              </p>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={handleBackToLogin}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-opacity duration-150"
              >
                {AUTH_MESSAGES.BUTTONS.BACK_TO_LOGIN}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg dark:bg-dark-theme-bg px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary shadow-xl rounded-lg p-8">
            <div className="text-center mb-6">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text mb-2">
                {AUTH_MESSAGES.PASSWORD_RESET.SUCCESS_TITLE}
              </h2>
              <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">
                {AUTH_MESSAGES.PASSWORD_RESET.PASSWORD_UPDATED}
              </p>
            </div>
            
            <button
              onClick={handleBackToLogin}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-opacity duration-150"
            >
              {AUTH_MESSAGES.BUTTONS.CONTINUE} to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-bg dark:bg-dark-theme-bg px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary shadow-xl rounded-lg p-8">
          <div className="text-center mb-6">
            <LockClosedIcon className="h-12 w-12 text-primary-light dark:text-primary-dark mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-theme-text dark:text-dark-theme-text">
              {AUTH_MESSAGES.PASSWORD_RESET.CONFIRM_TITLE}
            </h2>
            <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary mt-2">
              {AUTH_MESSAGES.PASSWORD_RESET.CONFIRM_DESCRIPTION}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">
                {AUTH_MESSAGES.LABELS.NEW_PASSWORD}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setShowRequirements(true)}
                  onBlur={() => setShowRequirements(false)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
                  placeholder={AUTH_MESSAGES.PLACEHOLDERS.NEW_PASSWORD}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {password && (
                <div className="mt-2">
                  <PasswordStrengthIndicator password={password} />
                </div>
              )}
              {showRequirements && (
                <div className="mt-2">
                  <PasswordRequirements password={password} />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">
                {AUTH_MESSAGES.LABELS.CONFIRM_PASSWORD}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
                  placeholder={AUTH_MESSAGES.PLACEHOLDERS.CONFIRM_PASSWORD}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {AUTH_MESSAGES.PASSWORD_RESET.PASSWORDS_DONT_MATCH}
                </p>
              )}
            </div>

            {/* Rate Limit Feedback */}
            {authError && isRateLimitError(authError) && (authError as AuthError).rateLimitInfo && (
              <RateLimitFeedback
                rateLimitInfo={(authError as AuthError).rateLimitInfo!}
                onRetryAvailable={handleRateLimitRetry}
              />
            )}

            {/* General Error Display */}
            {authError && !isRateLimitError(authError) && (
              <div className={`p-3 rounded-md ${
                (authError as AuthError).type === 'validation'
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}>
                <p className={`text-sm ${
                  (authError as AuthError).type === 'validation'
                    ? 'text-amber-700 dark:text-amber-300'
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  {(authError as AuthError).message}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                type="submit"
                disabled={passwordSubmitting || !password || !confirmPassword || password !== confirmPassword || (passwordStrength?.level === 'Very Weak')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
              >
                {passwordSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {AUTH_MESSAGES.PASSWORD_RESET.UPDATING_PASSWORD}
                  </>
                ) : (
                  AUTH_MESSAGES.BUTTONS.UPDATE_PASSWORD
                )}
              </button>

              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full flex justify-center py-2 px-4 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary bg-theme-bg dark:bg-dark-theme-bg hover:bg-theme-bg-tertiary dark:hover:bg-dark-theme-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-colors duration-150"
              >
                {AUTH_MESSAGES.BUTTONS.CANCEL}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;